import { useState } from "preact/compat"

import { LedgerAccount } from "@/api"
import { api_fetch } from "@/api/client"
import { IntegrationWithJobInfo, NetsuiteConfig, SageIntacctConfig, SageLocation, getSageJournals, updateIntegration } from "@/api/integration"
import { OpenDeductionsScope } from "@/api/accounting.tsx"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { LedgerAccountSelect } from "@/components/ledger_account_select"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"

const NONE_VALUE = "__none__"

export type OpenDeductionsProvider = "sageintacct" | "netsuite"

/** User-facing copy for each parking scope, in the order the picker lists them. */
export const OPEN_DEDUCTIONS_SCOPE_OPTIONS: { value: OpenDeductionsScope; label: string }[] = [
  {
    value: "by_stage",
    label: "Stage decides — Validated/Expensable, fully coded deductions post to their GL accounts; everything else parks until its stage is closed out",
  },
  { value: "all_deductions", label: "Everything parks — a deduction syncs as soon as it is coded" },
  { value: "uncoded_only", label: "Only unfinished ones park — coded deductions post directly when cash is applied" },
]

/** The scope a provider defaults to when none has been saved yet. */
export function default_open_deductions_scope(provider: OpenDeductionsProvider): OpenDeductionsScope {
  return provider === "netsuite" ? "by_stage" : "all_deductions"
}

interface OpenDeductionsSettingsProps {
  integration: IntegrationWithJobInfo
  provider: OpenDeductionsProvider
}

/**
 * The "post now, classify later" switch, shared by Sage Intacct and NetSuite: pick the GL account
 * cash application parks deductions in (the open-deductions holding account) and what makes a parked
 * deduction syncable. Sage additionally needs the journal (e.g. GJ) its reclassification entries
 * post to. Leaving the account unset keeps the classic behavior — every deduction must be coded
 * before a check can be applied.
 */
export function OpenDeductionsSettings({ integration, provider }: OpenDeductionsSettingsProps) {
  const config = integration.data as SageIntacctConfig & NetsuiteConfig
  const [accounts, setAccounts] = useState<LedgerAccount[]>([])
  const [journals, setJournals] = useState<SageLocation[]>([])
  const [account_id, setAccountId] = useState<string | null>(config.open_deductions_account_id ?? null)
  const [journal_id, setJournalId] = useState<string | null>(config.gl_journal_id ?? null)
  const [scope, setScope] = useState<OpenDeductionsScope>(config.open_deductions_scope ?? default_open_deductions_scope(provider))
  // Sage only: one holding-account line per parked deduction (the default) or one combined line.
  const [separate_lines, setSeparateLines] = useState<boolean>(config.open_deductions_line_grouping !== "combined")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useAsyncEffect(async () => {
    const [accounts_res, journals_res] = await Promise.all([
      api_fetch<LedgerAccount[]>("/reason_codes"),
      provider === "sageintacct" ? getSageJournals(integration.id) : Promise.resolve(null),
    ])
    if (accounts_res.ok) {
      setAccounts(accounts_res.value.data)
    }
    if (journals_res && journals_res.ok) {
      setJournals(journals_res.value.data)
    }
    setLoading(false)
  }, [integration.id, provider])

  async function handleSave() {
    setSaving(true)
    // Empty strings clear a setting — the settings merge ignores nulls.
    const data: Record<string, string> = { open_deductions_account_id: account_id ?? "", open_deductions_scope: scope }
    if (provider === "sageintacct") {
      data.gl_journal_id = journal_id ?? ""
      data.open_deductions_line_grouping = separate_lines ? "per_deduction" : "combined"
    }
    const res = await updateIntegration(integration.id, { data })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Save failed", description: "Could not save the open-deductions settings.", variant: "destructive" })
      return
    }
    toast({ title: "Settings saved", description: "Open-deductions settings updated." })
  }

  if (loading) {
    return <div class="text-gray-500 text-sm">Loading accounts…</div>
  }

  const system = provider === "netsuite" ? "NetSuite" : "Sage"

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">
        With an open-deductions account set, applying a check books its deductions to that account so cash posts daily without
        waiting on coding or on approval to expense. MarginWiz keeps the real GL coding the whole time; parked deductions are then
        reclassified to their GL accounts in batches from the Open Deductions screen, which posts journal entries to {system}
        {provider === "netsuite" ? " (one line per deduction: GL, customer, description and reference date)" : ""}. The scope
        setting decides what parks and what makes a parked deduction ready to sync.
      </p>
      <div class="space-y-1 max-w-md">
        <Label>Open-deductions holding account</Label>
        <LedgerAccountSelect
          value={account_id}
          onValueChange={setAccountId}
          accounts={accounts}
          none_label="None (require coding before applying)"
        />
        {provider === "netsuite" && (
          <p class="text-xs text-gray-500">
            The credit memo line posts through the NetSuite item mapped on this ledger account, so the account needs an Item
            mapping (Settings → Synced data).
          </p>
        )}
      </div>
      <div class="space-y-1 max-w-md">
        <Label>What parks, and when it syncs</Label>
        <Select value={scope} onValueChange={(v: string) => setScope(v as OpenDeductionsScope)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OPEN_DEDUCTIONS_SCOPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {provider === "sageintacct" && (
          <p class="text-xs text-gray-500">
            Multi-DC checks (invoices spanning several Sage customers) always park everything: the per-customer attribution is what
            the holding account tracks.
          </p>
        )}
      </div>
      {provider === "sageintacct" && (
        <div class="flex items-start gap-3 max-w-md">
          <Switch
            checked={separate_lines}
            onCheckedChange={(checked: boolean) => setSeparateLines(checked)}
            aria-label="Post each open deduction as its own line"
          />
          <div class="space-y-1">
            <Label>Post each open deduction as its own line</Label>
            <p class="text-xs text-gray-500">
              On: every parked deduction gets its own holding-account line on the credit memo, so each reclassification ties
              one-to-one to the line that put it there. Off: one combined line per credit memo, with the invoice numbers listed
              in its memo.
            </p>
          </div>
        </div>
      )}
      {provider === "sageintacct" && (
        <div class="space-y-1 max-w-md">
          <Label>Journal for reclassification entries</Label>
          <Select value={journal_id ?? NONE_VALUE} onValueChange={(v: string) => setJournalId(v === NONE_VALUE ? null : v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select journal (e.g. GJ)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>Not set</SelectItem>
              {journals.map((journal: SageLocation) => (
                <SelectItem key={journal.id} value={journal.id}>
                  {journal.name ? `${journal.id} — ${journal.name}` : journal.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <Button variant="outline" disabled={saving} onClick={handleSave}>
        {saving ? "Saving…" : "Save open-deductions settings"}
      </Button>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("default_open_deductions_scope", () => {
    it("gates NetSuite on the stage and keeps Sage review-first", () => {
      expect(default_open_deductions_scope("netsuite")).toBe("by_stage")
      expect(default_open_deductions_scope("sageintacct")).toBe("all_deductions")
    })
  })
}
